import imageExtensions from "image-extensions";
import isUrl from "is-url";
import type { JSX } from "preact";
import { useState } from "preact/compat";
import type { Editor } from "slate";
import { Transforms } from "slate";
import { useSlate } from "slate-react";
import { ConfirmModal } from "@gss/components/ConfirmModal";
import { InputText } from "@gss/components/InputText";
import { Popover } from "@gss/components/Popover";
import { Image } from "../components/icons";
import type { ImageElement } from "./types";
import { getNode, isActive, removeNodes } from "./util";

export function AddImageButton(): JSX.Element {
    const editor = useSlate();

    const onSave = (url: string) => {
        const image = getImageElement(editor);
        if (image) {
            // Update existing image
            if (url) {
                updateImage(editor, image, url);
            }

            // Remove existing image
            else {
                removeImage(editor);
            }
        }

        // Insert new image
        else if (url) {
            insertImage(editor, url);
        }
    };

    return (
        <Popover
            overlay={(hide) => (
                <Overlay
                    resolve={(url) => {
                        onSave(url);
                        hide();
                    }}
                    reject={hide}
                    image={getImageElement(editor)}
                />
            )}
        >
            <button
                type="button"
                className={isImageActive(editor) ? "active" : undefined}
            >
                <Image />
            </button>
        </Popover>
    );
}

interface OverlayParameters {
    resolve: (url: string) => void;
    reject: () => void;
    image?: ImageElement;
}

function Overlay({ resolve, reject, image }: OverlayParameters) {
    const [value, setValue] = useState(image?.src ?? "");
    const url = value.trim();
    const isEmpty = url === "";
    const isValid = isEmpty || isImageUrl(url);

    return (
        <ConfirmModal
            disabled={!isValid || isEmpty}
            resolve={() => resolve(url)}
            reject={reject}
        >
            <InputText
                small
                invalid={!isValid}
                value={value}
                placeholder="Skriv adress"
                onChange={setValue}
            />
        </ConfirmModal>
    );
}

export function withImages(editor: Editor): Editor {
    const { insertData, isVoid } = editor;

    editor.isVoid = (element) => {
        return element.type === "image" ? true : isVoid(element);
    };

    editor.insertData = (data) => {
        const text = data.getData("text/plain");
        const { files } = data;

        if (files.length > 0) {
            for (const file of files) {
                const reader = new FileReader();
                const [mime] = file.type.split("/");

                if (mime === "image") {
                    reader.addEventListener("load", () => {
                        const src = reader.result;
                        if (typeof src === "string") {
                            insertImage(editor, src);
                        }
                    });

                    reader.readAsDataURL(file);
                }
            }
        } else if (isImageUrl(text)) {
            insertImage(editor, text);
        } else {
            insertData(data);
        }
    };

    return editor;
}

function isImageActive(editor: Editor): boolean {
    return isActive(editor, "image");
}

function getImageElement(editor: Editor): ImageElement | undefined {
    const node = getNode(editor, "image");
    return node?.[0];
}

function isImageUrl(url: string): boolean {
    if (!isUrl(url)) {
        return false;
    }
    const ext = new URL(url).pathname.split(".").pop();
    return ext != null && imageExtensions.includes(ext);
}

function updateImage(editor: Editor, oldImage: ImageElement, src: string) {
    removeImage(editor);
    insertImage(editor, src);
}

function insertImage(editor: Editor, src: string) {
    const image: ImageElement = {
        type: "image",
        src,
        children: [{ text: "" }],
    };
    Transforms.insertNodes(editor, image);
}

function removeImage(editor: Editor) {
    removeNodes(editor, "image");
}
